/**
 * Listening documents read by the device's voices (D-106), shared by the web and the phone:
 * the lines of a script (who speaks, with which kind of voice), the flow of a TCF CO question
 * and the choice of a different voice per character. No platform API here: the web speaks with
 * speechSynthesis, the phone with expo-speech.
 *
 * The server sends the lines already split (`script_for_speech`: [{speaker, gender, role, text,
 * index}]) with the same rules as the generated audio; a plain-text script (an older server,
 * an IELTS part, an exercise) is split here: « Label : text » lines, the labels and the markers
 * « (F) », « (H) », « (E) », « (QC) »… are never read, and the scene « [Radio…] » neither.
 */

export type LineGender = "female" | "male" | "girl" | "boy";
export type LineRole = "character" | "announcer" | "instructions";

export interface SpokenLine {
  /** Name shown in the transcript ("" for an unnamed speaker or the instructions). */
  speaker: string;
  gender: LineGender;
  role: LineRole;
  text: string;
  /** Same number for every line of one speaker (-1: the instructions voice). */
  index: number;
}

export interface CoFlow {
  consigne: string;
  readingSeconds: number;
  answerSeconds: number;
  readConsigne: boolean;
  readQuestion: boolean;
}

const GENDERS: readonly LineGender[] = ["female", "male", "girl", "boy"];
const ROLES: readonly LineRole[] = ["character", "announcer", "instructions"];
/** A title written with a dot may open a label (« M. TREMBLAY (H) : », « Dr. Hale: »), D-114. */
const TITLES = "(?:M|MM|Mme|MME|Mlle|MLLE|Mr|MR|Mrs|MRS|Ms|MS|Dr|DR|Pr|PR|Prof|PROF|Me|ME|St|ST|Ste|STE)";
const LABEL = new RegExp(String.raw`^\s*((?:${TITLES}\.\s*)?[^\s\d:.!?][^:.!?\n]{0,39}?)\s*:\s*(\S.*)$`, "u");
const MARKERS = /\(([^)]*)\)/g;
const SCENE = /\[[^\]\n]*\]/g;

/** Words of the labels of the older scripts (the server has the full lists). */
const LABEL_HINTS: Record<LineGender, readonly string[]> = {
  female: ["femme", "mere", "maman", "cliente", "madame", "mme", "mlle", "woman", "mother", "mrs", "ms", "miss", "girl"],
  male: ["homme", "pere", "papa", "client", "monsieur", "m", "man", "father", "mr", "boy"],
  girl: ["fille", "fillette"],
  boy: ["garcon", "fils"],
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function fold(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

function labelGender(label: string): LineGender | null {
  const tags = [...label.matchAll(MARKERS)].flatMap((m) => fold(m[1] ?? "").split(/[,\s/]+/));
  const child = tags.includes("e") || tags.includes("enfant");
  if (tags.includes("f") || tags.includes("femme")) return child ? "girl" : "female";
  if (tags.includes("h") || tags.includes("m") || tags.includes("homme")) return child ? "boy" : "male";
  const words = fold(label.replace(MARKERS, " ")).split(/[^a-z]+/).filter(Boolean);
  for (const gender of ["girl", "boy", "female", "male"] as const) {
    if (words.some((w) => LABEL_HINTS[gender].includes(w))) return gender;
  }
  return child ? "girl" : null;
}

/** A plain-text script → its spoken lines (labels, markers and scene never read). */
export function splitScript(script: string): SpokenLine[] {
  const raw = script
    .replace(SCENE, " ")
    .split("\n")
    .map((line) => line.replace(/\s{2,}/g, " ").trim())
    .filter(Boolean);
  const labelled = raw.filter((line) => LABEL.test(line)).length;
  const turns: { label: string; text: string }[] = [];
  for (const line of raw) {
    const match = LABEL.exec(line);
    const label = match?.[1]?.trim() ?? "";
    const isLabel = Boolean(match) && label.split(/\s+/).length <= 5 && (labelled > 1 || MARKERS.test(label) || !label.includes(" "));
    MARKERS.lastIndex = 0;
    if (match && isLabel) turns.push({ label, text: (match[2] ?? "").trim() });
    else if (turns.length) {
      const last = turns[turns.length - 1];
      if (last) last.text = `${last.text} ${line}`;
    } else turns.push({ label: "", text: line });
  }
  const order: string[] = [];
  const genders = new Map<string, LineGender>();
  let next: "female" | "male" = "female";
  for (const { label } of turns) {
    if (order.includes(label)) continue;
    order.push(label);
    const known = labelGender(label);
    genders.set(label, known ?? next);
    const adult: "female" | "male" = known === "male" ? "male" : known === null ? next : "female";
    next = adult === "female" ? "male" : "female";
  }
  return turns.map(({ label, text }) => ({
    speaker: label.replace(MARKERS, " ").replace(/\s+/g, " ").trim(),
    gender: genders.get(label) ?? "female",
    role: "character",
    text,
    index: order.indexOf(label),
  }));
}

/** The lines sent by the server, or a plain-text script split here. */
export function parseSpokenLines(value: unknown): SpokenLine[] {
  if (typeof value === "string") return value.trim() ? splitScript(value) : [];
  if (!Array.isArray(value)) return [];
  return value.filter(isObject).flatMap((row): SpokenLine[] => {
    const text = typeof row.text === "string" ? row.text.trim() : "";
    if (!text) return [];
    const gender = GENDERS.find((g) => g === row.gender) ?? "female";
    const role = ROLES.find((r) => r === row.role) ?? "character";
    return [
      {
        speaker: typeof row.speaker === "string" ? row.speaker : "",
        gender,
        role,
        text,
        index: typeof row.index === "number" ? row.index : 0,
      },
    ];
  });
}

/** The flow of a CO question sent by the server (null for an older server). */
export function parseCoFlow(value: unknown): CoFlow | null {
  if (!isObject(value)) return null;
  const seconds = (v: unknown, fallback: number) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : fallback);
  return {
    consigne: typeof value.consigne === "string" ? value.consigne : "",
    readingSeconds: seconds(value.reading_seconds, 0),
    answerSeconds: seconds(value.answer_seconds, 0),
    readConsigne: value.read_consigne === true,
    readQuestion: value.read_question === true,
  };
}

/** What the device reads for one question, as at the test centre: the instruction, the
 * document, then the question, both by the instructions voice (index -1). */
export function questionProgram(lines: readonly SpokenLine[], flow: CoFlow | null, stem: string): SpokenLine[] {
  const instructions = (text: string): SpokenLine => ({ speaker: "", gender: "female", role: "instructions", text, index: -1 });
  const out: SpokenLine[] = [];
  if (flow?.readConsigne && flow.consigne.trim()) out.push(instructions(flow.consigne));
  out.push(...lines);
  if (flow?.readQuestion && stem.trim()) out.push(instructions(stem));
  return out;
}

// --- One voice per character ------------------------------------------------------------------

export interface DeviceVoice {
  id: string;
  name: string;
  lang: string;
}

export interface VoiceChoice {
  voiceId: string | undefined;
  pitch: number;
  rate: number;
}

const FEMALE_NAMES = [
  "amelie", "audrey", "aurelie", "marie", "julie", "hortense", "denise", "celine", "virginie", "lea",
  "sylvie", "chantal", "caroline", "helene", "eloise", "vivienne", "brigitte", "sophie", "claire",
  "female", "femme", "woman", "samantha", "serena", "kate", "karen", "susan", "moira", "fiona",
  "tessa", "sonia", "libby", "hazel", "amy", "emma", "alba", "jenny", "aria", "sara", "joana",
];
const MALE_NAMES = [
  "thomas", "paul", "henri", "claude", "nicolas", "daniel", "mathieu", "jacques", "remy", "antoine",
  "jean", "gerard", "alain", "yves", "fabrice", "jerome", "male", "homme", "man", "arthur", "oliver",
  "george", "ryan", "alan", "guy", "eric", "rishi", "aaron", "fred", "gordon", "lee", "reed",
];

/** The gender of a device voice from its name, when it tells (undefined otherwise). */
export function voiceGender(name: string): "female" | "male" | undefined {
  const words = fold(name).split(/[^a-z]+/);
  if (words.some((w) => FEMALE_NAMES.includes(w))) return "female";
  if (words.some((w) => MALE_NAMES.includes(w))) return "male";
  return undefined;
}

/** Pitch of each kind of voice (the device's pitch is 0.5 to 2). */
const PITCH: Record<LineGender, number> = { female: 1.05, male: 0.85, girl: 1.5, boy: 1.35 };

/**
 * A voice per speaker index (-1 for the instructions), among the device's voices of the
 * language (`lang` « fr » or « en »): a woman gets a woman's voice, a man a man's voice, a child
 * a woman's voice raised; two speakers get two different voices while there are enough, then
 * different pitches and rates (a device with a single voice still gives each one its sound).
 */
export function planVoices(
  lines: readonly SpokenLine[],
  available: readonly DeviceVoice[],
  lang: string,
  preferred: string,
  baseRate = 0.95,
): Map<number, VoiceChoice> {
  const code = (v: DeviceVoice) => v.lang.replace("_", "-").toLowerCase();
  const own = available.filter((v) => code(v).startsWith(lang));
  const ordered = [...own.filter((v) => code(v) === preferred.toLowerCase()), ...own.filter((v) => code(v) !== preferred.toLowerCase())];
  const pools = {
    female: ordered.filter((v) => voiceGender(v.name) === "female"),
    male: ordered.filter((v) => voiceGender(v.name) === "male"),
    any: ordered,
  };
  const used = new Map<string, number>();
  const plan = new Map<number, VoiceChoice>();
  const speakers: { index: number; gender: LineGender; role: LineRole }[] = [];
  for (const line of lines) {
    if (!speakers.some((s) => s.index === line.index)) speakers.push({ index: line.index, gender: line.gender, role: line.role });
  }
  // the characters choose first; the instructions voice takes what is left
  speakers.sort((a, b) => Number(a.role === "instructions") - Number(b.role === "instructions"));
  for (const speaker of speakers) {
    const adult = speaker.gender === "male" ? "male" : "female";
    const other = adult === "male" ? "female" : "male";
    const pool = pools[adult].length ? pools[adult] : pools.any;
    // a free voice of the gender, else a free voice of unknown gender, else the gender again
    // (another pitch), else any free voice
    const neutral = ordered.filter((v) => voiceGender(v.name) === undefined);
    const fresh =
      pool.find((v) => !used.has(v.id)) ??
      neutral.find((v) => !used.has(v.id)) ??
      (pools[adult].length ? undefined : ordered.find((v) => !used.has(v.id) && voiceGender(v.name) !== other));
    const voice = fresh ?? pool[0] ?? ordered[0];
    const repeats = voice ? (used.get(voice.id) ?? 0) : speakers.indexOf(speaker);
    if (voice) used.set(voice.id, repeats + 1);
    const known = voice ? voiceGender(voice.name) : undefined;
    // the same voice again, or a voice of the other gender: the pitch makes the difference
    let pitch = speaker.role === "instructions" ? 1 : PITCH[speaker.gender];
    if (known && known !== adult) pitch = adult === "male" ? 0.75 : 1.25;
    if (speaker.role !== "instructions") pitch += repeats * (adult === "male" ? -0.08 : 0.08);
    const rate = baseRate * (speaker.role === "instructions" ? 1 : 1 + ((repeats % 2) * 2 - 1) * (repeats ? 0.06 : 0));
    plan.set(speaker.index, {
      voiceId: voice?.id,
      pitch: Math.round(Math.min(2, Math.max(0.5, pitch)) * 100) / 100,
      rate: Math.round(rate * 100) / 100,
    });
  }
  return plan;
}

// --- Device voices that never hang (D-114) -----------------------------------------------------

/** Longest piece given to a device voice at once: Chrome stops a long utterance after about
 * fifteen seconds without ever firing « end », which froze the player and the examiner. */
export const SPEECH_CHUNK_CHARS = 180;

/** A text cut at the end of its sentences (then at its commas) into pieces of at most `max`
 * characters, read one after the other. */
export function speechChunks(text: string, max = SPEECH_CHUNK_CHARS): string[] {
  const clean = text.replace(/\s+/g, " ").trim();
  if (!clean) return [];
  if (clean.length <= max) return [clean];
  const sentences = clean.split(/(?<=[.!?…;:])\s+/u);
  const pieces: string[] = [];
  for (const sentence of sentences) {
    if (sentence.length <= max) {
      pieces.push(sentence);
      continue;
    }
    let current = "";
    for (const part of sentence.split(/(?<=,)\s+/u)) {
      if (current && current.length + part.length + 1 > max) {
        pieces.push(current);
        current = "";
      }
      if (part.length > max) {
        // no punctuation at all: cut between two words
        for (const word of part.split(" ")) {
          if (current && current.length + word.length + 1 > max) {
            pieces.push(current);
            current = "";
          }
          current = current ? `${current} ${word}` : word;
        }
      } else current = current ? `${current} ${part}` : part;
    }
    if (current) pieces.push(current);
  }
  // short neighbours read together (fewer pauses)
  const merged: string[] = [];
  for (const piece of pieces) {
    const last = merged[merged.length - 1];
    if (last !== undefined && last.length + piece.length + 1 <= max) merged[merged.length - 1] = `${last} ${piece}`;
    else merged.push(piece);
  }
  return merged;
}

/**
 * D-181: the file a private link opens. The server signs the same file again at each refresh of a
 * page's data (`/files/<file>:<time>:<signature>/`): only the part before the time names it.
 */
export function fileOfLink(url: string): string {
  const match = /\/files\/([^:/]+)/.exec(url);
  return match?.[1] ?? url;
}

/** Longest time a device voice may take for a piece before it is given up (its « end » event
 * never came): about 120 ms a character at the normal speed, never less than 4 s. */
export function speechWatchdogMs(text: string, rate = 1): number {
  return Math.round(Math.max(4000, (text.length * 120) / Math.max(rate, 0.5)) + 3000);
}
